// ─────────────────────────────────────────────────────────────────────────────
// DetectorActionsMenu
//
// A kebab (⋮) button that opens a small popover. Lets the user mark a detector
// as Acknowledged, Working on it, Dismissed, or reset back to Active.
//
// Self-contained: owns its own open/close state, click-outside dismissal,
// escape-key handling, and the inline "Working on it" target form. The
// parent passes:
//
//   detector  — the full detector result (used to extract current metric value)
//   state     — the resolved detector_state row from useDetectorStates.resolve()
//   accountId — current account scope
//   onChange  — async callback (args) => Promise. Invoked when user picks an
//               action. Caller routes this to useDetectorStates.setStatus().
//
// Why a popover, not three inline buttons:
//   - scales as we add actions over time (view history, snooze, share, etc.)
//   - separates "state metadata" from "card content" visually
//   - hides Dismiss one click away so it's not a misclick magnet
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { C } from "../../lib/colors";
import { getTargetDescriptor } from "../../lib/detectorState";

// ─── Status visuals ─────────────────────────────────────────────────────────
const STATUS_META = {
  active:         { label: "Active",            color: C.muted, dot: null },
  acknowledged:   { label: "Acknowledged",      color: C.blue,  dot: "•"  },
  working_on_it:  { label: "Working on it",     color: C.green, dot: "→"  },
  dismissed:      { label: "Dismissed",         color: C.muted, dot: "×"  },
};

// ─── Main component ─────────────────────────────────────────────────────────
export function DetectorActionsMenu({ detector, state, accountId, onChange }) {
  const [open, setOpen] = useState(false);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const containerRef = useRef(null);
  const status = state?.status || "active";

  // Close on outside click + Esc. Both bindings are needed; just outside-click
  // alone leaves the menu open if you tab into it then press Esc.
  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setShowTargetForm(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setShowTargetForm(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const meta = STATUS_META[status] || STATUS_META.active;
  const targetDescriptor = getTargetDescriptor(detector.id);

  // Generic handler that closes the menu + writes status
  const apply = async (args) => {
    setOpen(false);
    setShowTargetForm(false);
    if (onChange) await onChange(args);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      {/* Kebab button */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Detector actions"
        aria-expanded={open}
        style={{
          background: open ? `${C.muted}22` : "transparent",
          border: "none",
          padding: "4px 8px",
          borderRadius: 6,
          cursor: "pointer",
          color: meta.color,
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1,
        }}
        title={status === "active" ? "Mark this pattern" : `Status: ${meta.label}`}
      >
        ⋮
      </button>

      {/* Popover */}
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            minWidth: showTargetForm ? 280 : 200,
            background: "#11141f",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            zIndex: 50,
            overflow: "hidden",
          }}
        >
          {/* Current status header */}
          <div style={{
            padding: "8px 12px",
            fontSize: 10,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            borderBottom: `1px solid ${C.border}`,
            background: "#0b0d19",
          }}>
            Status: <span style={{ color: meta.color, fontWeight: 700 }}>{meta.label}</span>
          </div>

          {/* Guard: refuse to write state when no account is selected.
              We deliberately don't fall back to "user-wide" — that semantics
              should be explicit (a future "Apply to all accounts" toggle),
              not silent. Surfacing this as a hint also helps the user
              diagnose any place where the dropdown's display value and
              `currentAccountId` get out of sync. */}
          {!accountId && (
            <div style={{
              padding: "10px 12px",
              fontSize: 11,
              color: C.yellow,
              background: `${C.yellow}11`,
              borderBottom: `1px solid ${C.border}`,
              lineHeight: 1.5,
            }}>
              Select an account in the dropdown above before marking this pattern.
            </div>
          )}

          {!showTargetForm && (
            <>
              <MenuItem
                onClick={() => apply({ accountId, detectorId: detector.id, status: "acknowledged" })}
                disabled={!accountId || status === "acknowledged"}
                icon="•"
                color={C.blue}
                label="Acknowledge"
                hint="I've seen it. Stop being loud."
              />
              {targetDescriptor && (
                <MenuItem
                  onClick={() => setShowTargetForm(true)}
                  disabled={!accountId}
                  icon="→"
                  color={C.green}
                  label={status === "working_on_it" ? "Update target" : "I'm working on it"}
                  hint="Set a target & deadline."
                />
              )}
              <MenuItem
                onClick={() => apply({ accountId, detectorId: detector.id, status: "dismissed" })}
                disabled={!accountId || status === "dismissed"}
                icon="×"
                color={C.muted}
                label="Dismiss"
                hint="Hide. Not a real pattern."
              />
              {status !== "active" && (
                <>
                  <div style={{ borderTop: `1px solid ${C.border}` }} />
                  <MenuItem
                    onClick={() => apply({ accountId, detectorId: detector.id, status: "active" })}
                    disabled={!accountId}
                    icon="↺"
                    color={C.muted}
                    label="Reset to active"
                    hint="Clear the current status."
                  />
                </>
              )}
            </>
          )}

          {showTargetForm && targetDescriptor && (
            <TargetForm
              detector={detector}
              accountId={accountId}
              existing={state}
              descriptor={targetDescriptor}
              onCancel={() => setShowTargetForm(false)}
              onSubmit={apply}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── MenuItem ───────────────────────────────────────────────────────────────
function MenuItem({ onClick, disabled, icon, color, label, hint }) {
  return (
    <button
      role="menuitem"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: "block",
        width: "100%",
        padding: "10px 12px",
        background: "transparent",
        border: "none",
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        color: C.text,
        fontSize: 13,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = `${color}11`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color, fontWeight: 700, width: 14, textAlign: "center" }}>{icon}</span>
        <span style={{ fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2, marginLeft: 22 }}>{hint}</div>
    </button>
  );
}

// ─── Target form (Working on it) ────────────────────────────────────────────
//
// Captures the user's target_value + target_date for this detector. Both
// fields default to sensible values pulled from the descriptor + current
// metric, so submitting without edits still produces a meaningful row.
//
// We snapshot the *current* metric value as `target_baseline` at submit time
// — that's what progress is measured against later.
function TargetForm({ detector, accountId, existing, descriptor, onCancel, onSubmit }) {
  const currentValue = descriptor.extract(detector);
  const initialTarget = existing?.target_value != null
    ? Number(existing.target_value)
    : descriptor.defaultTarget(currentValue);

  // Default deadline: 30 days from today (or existing target_date if there is one)
  const defaultDateStr = () => {
    if (existing?.target_date) return existing.target_date;
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  };

  const [targetValue, setTargetValue] = useState(String(initialTarget));
  const [targetDate, setTargetDate] = useState(defaultDateStr);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedTarget = Number(targetValue);
    if (!Number.isFinite(parsedTarget)) return;
    setSubmitting(true);
    try {
      await onSubmit({
        accountId,
        detectorId:      detector.id,
        status:          "working_on_it",
        targetMetric:    descriptor.metric,
        targetValue:     parsedTarget,
        targetBaseline:  currentValue,           // freeze the "starting point"
        targetDate,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: 12 }}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
        {descriptor.label}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 12 }}>
        <span style={{ color: C.muted }}>Currently:</span>
        <span style={{ fontFamily: "monospace", color: C.text }}>
          {descriptor.formatter(currentValue)}
        </span>
      </div>

      <label style={{ display: "block", fontSize: 11, color: C.muted, marginBottom: 4 }}>
        Target {descriptor.direction === "down" ? "(or lower)" : "(or higher)"}
      </label>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
        <input
          type="number"
          step="any"
          value={targetValue}
          onChange={(e) => setTargetValue(e.target.value)}
          style={{
            flex: 1,
            padding: "6px 8px",
            background: "#0b0d19",
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            color: C.text,
            fontSize: 13,
            fontFamily: "monospace",
          }}
        />
        <span style={{ fontSize: 12, color: C.muted, minWidth: 30 }}>{descriptor.unit}</span>
      </div>

      <label style={{ display: "block", fontSize: 11, color: C.muted, marginBottom: 4 }}>
        By date
      </label>
      <input
        type="date"
        value={targetDate}
        onChange={(e) => setTargetDate(e.target.value)}
        style={{
          width: "100%",
          padding: "6px 8px",
          background: "#0b0d19",
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          color: C.text,
          fontSize: 13,
          fontFamily: "monospace",
          marginBottom: 12,
        }}
      />

      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1,
            padding: "8px",
            background: "transparent",
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            color: C.muted,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          style={{
            flex: 1,
            padding: "8px",
            background: C.green,
            border: "none",
            borderRadius: 6,
            color: "#0b0d19",
            cursor: submitting ? "default" : "pointer",
            fontSize: 12,
            fontWeight: 700,
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {submitting ? "Saving…" : "Set target"}
        </button>
      </div>
    </form>
  );
}
